export const limitDecimalInput = (rawValue, maxDecimals = 2) => {
  const str = String(rawValue ?? '')
  if (!str.includes('.')) return str
  const [whole, fraction = ''] = str.split('.')
  if (fraction.length <= maxDecimals) return str
  return `${whole}.${fraction.slice(0, maxDecimals)}`
}

export const formatPriceToTwoDecimals = (value) => {
  if (value === '' || value == null) return ''
  const num = parseFloat(value)
  if (Number.isNaN(num)) return String(value)
  return num.toFixed(2)
}

export const formatRetailPriceDisplay = (value) => {
  if (value === '' || value == null) return ''
  const num = parseFloat(value)
  if (Number.isNaN(num)) return String(value)
  return Number(num.toFixed(2)).toString()
}

const isWholeNumber = (value) => Math.abs(value - Math.round(value)) < 0.001

export const stepRetailPriceByArrow = (currentValue, direction, minValue = 0.01) => {
  const min = Math.max(0.01, Number(minValue) || 0.01)
  const num = parseFloat(currentValue)
  if (Number.isNaN(num)) {
    return formatRetailPriceDisplay(Math.max(min, 1))
  }

  let stepped
  if (direction === 'up') {
    stepped = isWholeNumber(num) ? num + 1 : Math.ceil(num - 0.0001)
  } else {
    stepped = isWholeNumber(num) ? num - 1 : Math.floor(num + 0.0001)
  }

  return formatRetailPriceDisplay(Math.max(min, stepped))
}

export const normalizeRetailArrowChange = (previousValue, newValue, minValue = 0.01) => {
  const prev = parseFloat(previousValue)
  const next = parseFloat(newValue)
  if (Number.isNaN(prev) || Number.isNaN(next)) {
    return limitDecimalInput(newValue)
  }

  const diff = Number((next - prev).toFixed(2))
  const isSingleStep =
    Math.abs(Math.abs(diff) - 1) < 0.001 ||
    Math.abs(Math.abs(diff) - 0.01) < 0.001

  if (isSingleStep) {
    return stepRetailPriceByArrow(
      previousValue,
      diff > 0 ? 'up' : 'down',
      minValue,
    )
  }

  return limitDecimalInput(newValue)
}

/** Product formula (recipe) is soft-deleted when status === 2. */
export const isProductFormulaActive = (product) => {
  const status = product?.recipe_status ?? product?.recipeStatus
  if (status == null || status === '') return true
  return Number(status) !== 2
}

/** Show beaker / open View Production Formula when formula exists and is not deleted. */
export const canShowProductFormulaIcon = (product, currentFacilityId, recipeIdOverride) => {
  if (!product || product?.isAdditionalProduct) return false
  const recipeId =
    recipeIdOverride ?? product?.recipe_id ?? product?.formula_id ?? product?.formulaId
  if (!recipeId) return false
  if (
    currentFacilityId != null &&
    product?.facility_id != null &&
    Number(product.facility_id) !== Number(currentFacilityId)
  ) {
    return false
  }
  return isProductFormulaActive(product)
}

export const getProductFormulaEach = (product) => {
  if (product?.inhouse === 0) {
    return Number(product?.sub_contract_price || 0)
  }
  return Number(
    (product?.expected_material_cost_with_markup || 0) +
      (product?.expected_service_cost_with_markup || 0),
  )
}

export const getSignedMarketingAdjustment = (product) => {
  const adj = Number(product?.adjustment) || 0
  const isPositive =
    product?.adj_value_type !== false &&
    product?.adj_value_type !== 0 &&
    product?.adj_value_type !== '0' &&
    product?.adj_value_type !== 'false'
  return isPositive ? adj : -adj
}

export const getCatalogProductOnlyRetailEach = (product) => {
  const fromActual = Number(
    product?.actual_retail_price ??
      product?.product_retail_price ??
      product?.max_price ??
      0,
  )
  if (fromActual > 0) return fromActual

  const retail = Number(product?.retail_price || 0)
  if (retail <= 0) return 0

  const installCost = Number(product?.install_cost) || 0
  if (installCost > 0 && retail >= installCost) {
    return Math.max(0, Number((retail - installCost).toFixed(2)))
  }

  return retail
}

const getDisplayedProductRetailEach = (product) => {
  if (product?.actual_retail_price != null && product?.actual_retail_price !== '') {
    return Number(product.actual_retail_price)
  }
  if (product?.product_retail_price != null && product?.product_retail_price !== '') {
    return Number(product.product_retail_price)
  }
  const base = getCatalogProductOnlyRetailEach(product)
  return Number((base + getSignedMarketingAdjustment(product)).toFixed(2))
}

const resolveOriginalActualRetail = (product) => {
  if (
    product?.original_actual_retail_price != null &&
    product?.original_actual_retail_price !== ''
  ) {
    return Number(product.original_actual_retail_price)
  }

  const signedAdj = getSignedMarketingAdjustment(product)
  const displayed = getDisplayedProductRetailEach(product)
  return Number((displayed - signedAdj).toFixed(2))
}

export const getDisplayedActualRetail = (product) => {
  const original = resolveOriginalActualRetail(product)
  return Number((original + getSignedMarketingAdjustment(product)).toFixed(2))
}

export const getDisplayedPrice = (product) => {
  if (product?.original_price != null) {
    return Number(product.original_price) + getSignedMarketingAdjustment(product)
  }
  return Number(product?.price ?? product?.actual_retail_price ?? product?.retail_price ?? 0)
}

export const getMarketingAdjustmentBase = (product, markupType) => {
  const displayed =
    markupType === 1 ? getDisplayedPrice(product) : getDisplayedActualRetail(product)
  const signedAdj = getSignedMarketingAdjustment(product)
  return Number((displayed - signedAdj).toFixed(2))
}

const resolveOriginalPrice = (product) => {
  if (product?.original_price != null) {
    const stored = Number(product.original_price)
    const signedAdj = getSignedMarketingAdjustment(product)
    const displayed = getDisplayedPrice(product)
    const expectedDisplay = Number((stored + signedAdj).toFixed(2))
    if (Math.abs(expectedDisplay - displayed) < 0.02) {
      return stored
    }
  }
  const displayed = getDisplayedPrice(product)
  const signedAdj = getSignedMarketingAdjustment(product)
  return Number((displayed - signedAdj).toFixed(2))
}

export const hasActiveInstallCost = (product) =>
  product?.install_cost != null &&
  product?.install_cost !== '' &&
  Number(product?.install_cost) !== 0

export const isInstallManuallyRemoved = (product) =>
  Boolean(
    product?.manually_updated_install_cost &&
    !hasActiveInstallCost(product),
  )

const resolveOriginalRetail = (product, originalActual) => {
  const signedInstallAdj = getSignedInstallMarketingAdjustment(product)
  const baseInstall = hasActiveInstallCost(product)
    ? product?.original_install_cost != null
      ? Number(product.original_install_cost)
      : Number((Number(product.install_cost) - signedInstallAdj).toFixed(2))
    : 0
  const computed = Number((originalActual + baseInstall).toFixed(2))

  if (product?.original_retail_price != null) {
    const stored = Number(product.original_retail_price)
    if (Math.abs(stored - computed) < 0.02) {
      return stored
    }
  }

  return computed
}

export const applyRetailPriceEdit = (product, displayedValue, markupType, options = {}) => {
  const { hasNegativePermission = false } = options
  const numValue = parseFloat(displayedValue)
  if (displayedValue === '' || Number.isNaN(numValue)) {
    return null
  }

  const formulaEach = getProductFormulaEach(product)
  const baseOriginalActual = resolveOriginalActualRetail(product)
  const baseOriginalPrice = resolveOriginalPrice(product)
  const baseOriginalRetail = resolveOriginalRetail(product, baseOriginalActual)
  const editableBase = markupType === 1 ? baseOriginalPrice : baseOriginalActual
  const installDelta = Number((baseOriginalRetail - baseOriginalActual).toFixed(2))

  if (numValue <= 0) {
    return {
      error: 'Retail value cannot be zero or less',
    }
  }

  if (numValue < formulaEach && !hasNegativePermission) {
    return {
      error: `Retail Price Each cannot be below Product formula cost ($${formulaEach.toFixed(2)})`,
    }
  }

  const signedDiff = Number((numValue - editableBase).toFixed(2))
  const isBackToBaseline = Math.abs(signedDiff) < 0.005
  const adj_value_type = isBackToBaseline ? true : signedDiff >= 0
  const adjustment = isBackToBaseline ? 0 : Number(Math.abs(signedDiff).toFixed(2))

  if (!isBackToBaseline && signedDiff < 0) {
    const maxNegative = hasNegativePermission
      ? editableBase
      : Math.max(0, editableBase - formulaEach)
    if (hasNegativePermission) {
      if (adjustment >= maxNegative - 0.001) {
        return {
          error: 'Retail value cannot be zero or less',
        }
      }
    } else if (adjustment - maxNegative > 0.01) {
      return {
        error: `Negative Adjustment Amount cannot reduce total below Product Formula Cost. Max allowed: $${maxNegative.toFixed(2)}`,
      }
    }
  }

  const signedAdj = adj_value_type ? adjustment : -adjustment
  const roundedValue = Number(numValue.toFixed(2))
  if (roundedValue <= 0) {
    return {
      error: 'Retail value cannot be zero or less',
    }
  }
  const newActualRetail =
    markupType === 2
      ? roundedValue
      : Number((baseOriginalActual + signedAdj).toFixed(2))
  const newRetail =
    markupType === 2
      ? Number((roundedValue + installDelta).toFixed(2))
      : Number((baseOriginalRetail + signedAdj).toFixed(2))

  return {
    original_actual_retail_price: baseOriginalActual,
    original_retail_price: baseOriginalRetail,
    original_product_retail_price: baseOriginalActual,
    original_price:
      markupType === 1 ? baseOriginalPrice : product.original_price ?? product.price,
    actual_retail_price: newActualRetail,
    product_retail_price: newActualRetail,
    retail_price: newRetail,
    price:
      markupType === 1
        ? roundedValue
        : Number((baseOriginalPrice + signedAdj).toFixed(2)),
    adjustment,
    adj_value_type,
    retail_price_edited: !isBackToBaseline,
  }
}

export const getSignedInstallMarketingAdjustment = (product) => {
  const adj = Number(product?.install_adjustment) || 0
  const isPositive =
    product?.install_adj_value_type !== false &&
    product?.install_adj_value_type !== 0 &&
    product?.install_adj_value_type !== '0' &&
    product?.install_adj_value_type !== 'false'
  return isPositive ? adj : -adj
}

export const getInstallFormulaEach = (product) =>
  Number(product?.expected_install_labor_cost_with_markup || 0)

export const getDisplayedInstallPrice = (product) => {
  if (product?.original_install_cost != null) {
    return (
      Number(product.original_install_cost) +
      getSignedInstallMarketingAdjustment(product)
    )
  }
  return Number(product?.install_cost ?? 0)
}

const resolveOriginalInstallCost = (product) => {
  if (product?.original_install_cost != null && product?.original_install_cost !== '') {
    return Number(product.original_install_cost)
  }

  const signedAdj = getSignedInstallMarketingAdjustment(product)
  const displayed = getDisplayedInstallPrice(product)
  return Number((displayed - signedAdj).toFixed(2))
}

export const applyInstallPriceEdit = (product, displayedValue, options = {}) => {
  const { hasNegativePermission = false } = options
  const numValue = parseFloat(displayedValue)
  if (displayedValue === '' || Number.isNaN(numValue)) {
    return null
  }

  const formulaEach = getInstallFormulaEach(product)
  const baseOriginalInstall = resolveOriginalInstallCost(product)
  const editableBase = baseOriginalInstall

  if (numValue <= 0) {
    return {
      error: 'Retail value cannot be zero or less',
    }
  }

  if (numValue < formulaEach && !hasNegativePermission) {
    return {
      error: `Install Price Each cannot be below Install formula cost ($${formulaEach.toFixed(2)})`,
    }
  }

  const signedDiff = Number((numValue - editableBase).toFixed(2))
  const isBackToBaseline = Math.abs(signedDiff) < 0.005
  const install_adj_value_type = isBackToBaseline ? true : signedDiff >= 0
  const install_adjustment = isBackToBaseline
    ? 0
    : Number(Math.abs(signedDiff).toFixed(2))

  if (!isBackToBaseline && signedDiff < 0) {
    const maxNegative = hasNegativePermission
      ? editableBase
      : Math.max(0, editableBase - formulaEach)
    if (hasNegativePermission) {
      if (install_adjustment >= maxNegative - 0.001) {
        return {
          error: 'Retail value cannot be zero or less',
        }
      }
    } else if (install_adjustment - maxNegative > 0.01) {
      return {
        error: `Negative Adjustment Amount cannot reduce total below Product Formula Cost. Max allowed: $${maxNegative.toFixed(2)}`,
      }
    }
  }

  const roundedValue = Number(numValue.toFixed(2))
  if (roundedValue <= 0) {
    return {
      error: 'Retail value cannot be zero or less',
    }
  }

  const baseOriginalActual = resolveOriginalActualRetail(product)
  const baseOriginalRetail = resolveOriginalRetail(product, baseOriginalActual)
  const signedProductAdj = getSignedMarketingAdjustment(product)
  const actualRetail = Number((baseOriginalActual + signedProductAdj).toFixed(2))
  const newRetail = Number((actualRetail + roundedValue).toFixed(2))

  return {
    original_install_cost: baseOriginalInstall,
    install_cost: roundedValue,
    install_adjustment,
    install_adj_value_type,
    install_price_edited: !isBackToBaseline,
    original_retail_price: baseOriginalRetail,
    retail_price: newRetail,
  }
}

export const distributeGroupTotalByFormulaShare = (members, getWeight, totalTarget) => {
  const targets = new Map()
  if (!members?.length) return targets

  const weights = members.map((member) => Math.max(0, Number(getWeight(member)) || 0))
  const totalWeight = weights.reduce((sum, weight) => sum + weight, 0)

  if (totalWeight > 0) {
    let allocated = 0
    members.forEach((member, idx) => {
      if (idx === members.length - 1) {
        targets.set(member.indexId, Number((totalTarget - allocated).toFixed(2)))
      } else {
        const value = Number(((weights[idx] / totalWeight) * totalTarget).toFixed(2))
        targets.set(member.indexId, value)
        allocated += value
      }
    })
    return targets
  }

  let allocated = 0
  const evenShare = Number((totalTarget / members.length).toFixed(2))
  members.forEach((member, idx) => {
    if (idx === members.length - 1) {
      targets.set(member.indexId, Number((totalTarget - allocated).toFixed(2)))
    } else {
      targets.set(member.indexId, evenShare)
      allocated += evenShare
    }
  })
  return targets
}

export const hydrateSavedOrderLinePrices = (savedRow, catalogDefaults = {}, options = {}) => {
  const retailSource = options?.retailSource || 'auto'
  let savedRetailRaw
  if (retailSource === 'design_order') {
    savedRetailRaw =
      savedRow?.actual_retail_price ??
      savedRow?.product_retail_price ??
      savedRow?.retail_price
  } else {
    // unit_cost is formula cost on estimate/sales lines — not the saved retail price
    savedRetailRaw =
      savedRow?.actual_retail_price ??
      savedRow?.product_retail_price ??
      savedRow?.retail_price
    if (savedRetailRaw == null || savedRetailRaw === '') {
      savedRetailRaw = savedRow?.unit_cost
    }
  }

  // When unit_cost was wrongly persisted as install_cost, prefer catalog/joined product retail from the response.
  const installNum = Number(savedRow?.install_cost)
  if (
    retailSource !== 'design_order' &&
    savedRow?.unit_cost != null &&
    savedRow?.unit_cost !== '' &&
    Number.isFinite(installNum) &&
    installNum > 0 &&
    Number(savedRow.unit_cost) === installNum
  ) {
    const responseActual =
      savedRow?.actual_retail_price ?? catalogDefaults?.actual_retail_price
    if (
      responseActual != null &&
      responseActual !== '' &&
      Number(responseActual) !== installNum
    ) {
      savedRetailRaw = responseActual
    } else if (
      savedRow?.product_retail_price != null &&
      savedRow?.product_retail_price !== '' &&
      Number(savedRow.product_retail_price) !== installNum
    ) {
      const productRetail = Number(savedRow.product_retail_price)
      savedRetailRaw =
        productRetail > installNum
          ? Number((productRetail - installNum).toFixed(2))
          : productRetail
    }
  }

  if (savedRetailRaw == null || savedRetailRaw === '') {
    return {}
  }

  const savedRetailEach = Number(savedRetailRaw)
  if (!Number.isFinite(savedRetailEach)) {
    return {}
  }

  const adjustmentFields = {
    adjustment: savedRow?.adjustment,
    adj_value_type: savedRow?.adj_value_type,
    install_adjustment: savedRow?.install_adjustment,
    install_adj_value_type: savedRow?.install_adj_value_type,
  }
  const signedProductAdj = getSignedMarketingAdjustment(adjustmentFields)
  const originalActual = Number((savedRetailEach - signedProductAdj).toFixed(2))
  const actualRetail = savedRetailEach

  const hydrated = {
    actual_retail_price: actualRetail,
    product_retail_price: actualRetail,
    original_actual_retail_price: originalActual,
    original_product_retail_price: originalActual,
    original_retail_price: originalActual,
    product_price: savedRow?.product_price ?? catalogDefaults?.price,
    price: savedRow?.product_price ?? savedRow?.price ?? catalogDefaults?.price,
    retail_price_edited: true,
    retail_price: actualRetail,
  }

  if (savedRow?.install_cost != null && savedRow?.install_cost !== '') {
    const savedInstall = Number(savedRow.install_cost)
    if (Number.isFinite(savedInstall)) {
      const signedInstallAdj = getSignedInstallMarketingAdjustment(adjustmentFields)
      const originalInstall = Number((savedInstall - signedInstallAdj).toFixed(2))
      hydrated.install_cost = savedInstall
      hydrated.original_install_cost = originalInstall
      hydrated.original_retail_price = Number((originalActual + originalInstall).toFixed(2))
      hydrated.retail_price = Number((actualRetail + savedInstall).toFixed(2))
      hydrated.install_price_edited = true
    }
  }

  return hydrated
}

export const normalizeProductForSave = (product) => {
  const signedProductAdj = getSignedMarketingAdjustment(product)
  const signedInstallAdj = getSignedInstallMarketingAdjustment(product)

  let actualRetail = product?.actual_retail_price
  let productRetail = product?.product_retail_price
  let installCost = product?.install_cost

  if (product?.original_actual_retail_price != null) {
    actualRetail = Number(
      (Number(product.original_actual_retail_price) + signedProductAdj).toFixed(2),
    )
    productRetail = actualRetail
  } else if (actualRetail != null) {
    productRetail = actualRetail
  }

  const installManuallyRemoved = isInstallManuallyRemoved(product)

  if (installManuallyRemoved) {
    installCost = null
  } else if (product?.original_install_cost != null && hasActiveInstallCost(product)) {
    installCost = Number(
      (Number(product.original_install_cost) + signedInstallAdj).toFixed(2),
    )
  } else if (!hasActiveInstallCost(product)) {
    installCost = null
  }

  const retailPrice =
    installCost != null && Number(installCost) > 0
      ? Number((Number(actualRetail || 0) + Number(installCost)).toFixed(2))
      : Number(actualRetail || 0)

  const normalized = {
    ...product,
    backendId: product?.backendId ?? product?.customer_order_product_id ?? null,
    actual_retail_price: actualRetail,
    product_retail_price: productRetail,
    install_cost: installManuallyRemoved ? null : installCost,
    original_install_cost: installManuallyRemoved ? null : product?.original_install_cost,
    retail_price: retailPrice,
    manually_updated_install_cost: installManuallyRemoved ? true : product?.manually_updated_install_cost,
  }

  delete normalized.retail_price_draft
  delete normalized.install_price_draft
  delete normalized.adjustment_draft
  delete normalized.install_adjustment_draft

  return normalized
}

export const clearCatalogInstallCost = (product) =>
  normalizeProductForSave({
    ...product,
    install_cost: null,
    original_install_cost: null,
    expected_install_labor_cost_with_markup: null,
    install_adjustment: 0,
    install_adj_value_type: true,
    manually_updated_install_cost: true,
  })

export const parseMarketingAdjValueTypeFromDb = (raw) => {
  if (raw === 0 || raw === false || raw === '0' || raw === 'false') return false
  if (raw === 1 || raw === true || raw === '1' || raw === 'true') return true
  if (raw != null && raw !== '') return Number(raw) !== 0
  return true
}

export const getCatalogPickerOriginalPrices = (catalog = {}) => {
  const productRetail = getCatalogProductOnlyRetailEach(catalog)
  const installCost = Number(catalog?.install_cost) || 0
  const signedInstallAdj = getSignedInstallMarketingAdjustment(catalog)
  const originalInstall =
    catalog?.original_install_cost != null
      ? Number(catalog.original_install_cost)
      : installCost > 0
        ? Math.max(0, Number((installCost - signedInstallAdj).toFixed(2)))
        : null
  const originalRetail =
    originalInstall != null && Number(originalInstall) > 0
      ? Number((productRetail + Number(originalInstall)).toFixed(2))
      : productRetail

  return {
    actual_retail_price: productRetail,
    product_retail_price: productRetail,
    retail_price: originalRetail,
    original_actual_retail_price: productRetail,
    original_product_retail_price: productRetail,
    original_retail_price: originalRetail,
    original_price: catalog?.original_price ?? Number(catalog?.price ?? productRetail),
    original_install_cost: originalInstall,
    adjustment: 0,
    adj_value_type: true,
    install_adjustment: catalog?.install_adjustment ?? 0,
    install_adj_value_type: catalog?.install_adj_value_type ?? true,
    retail_price_edited: false,
    install_price_edited: false,
  }
}

export const ensureCatalogBaselines = (product) => {
  const resolvedOriginalActual =
    product?.original_actual_retail_price != null &&
    product?.original_actual_retail_price !== ''
      ? Number(product.original_actual_retail_price)
      : resolveOriginalActualRetail(product)
  const resolvedOriginalPrice = resolveOriginalPrice(product)
  const resolvedOriginalRetail = resolveOriginalRetail(product, resolvedOriginalActual)
  const resolvedOriginalInstall =
    product?.original_install_cost != null && product?.original_install_cost !== ''
      ? Number(product.original_install_cost)
      : product?.install_cost != null && Number(product.install_cost) !== 0
        ? resolveOriginalInstallCost(product)
        : product?.original_install_cost ?? null

  return {
    ...product,
    original_actual_retail_price: resolvedOriginalActual,
    original_product_retail_price: resolvedOriginalActual,
    original_retail_price: resolvedOriginalRetail,
    original_price: product?.original_price ?? resolvedOriginalPrice,
    original_install_cost: resolvedOriginalInstall,
  }
}

export const getCatalogProductWithAdjustment = (data) => {
  if (!data) return null
  const result = { ...data }

  if (result.adj_value_type != null) {
    result.adj_value_type = parseMarketingAdjValueTypeFromDb(result.adj_value_type)
  }
  if (result.install_adj_value_type != null) {
    result.install_adj_value_type = parseMarketingAdjValueTypeFromDb(result.install_adj_value_type)
  }

  if (result.adj_value_type == null) result.adj_value_type = true
  if (result.adjustment == null) result.adjustment = 0
  if (result.markAdjType == null) result.markAdjType = 1

  const originalActual = resolveOriginalActualRetail(result)
  const originalRetail = resolveOriginalRetail(result, originalActual)
  const originalPrice = resolveOriginalPrice(result)
  let signedProductAdj = getSignedMarketingAdjustment(result)

  const displayedActual = getDisplayedProductRetailEach(result)
  if (
    Math.abs(displayedActual - originalActual) < 0.005 &&
    Math.abs(signedProductAdj) > 0.005
  ) {
    result.adjustment = 0
    result.adj_value_type = true
    result.retail_price_edited = false
    signedProductAdj = 0
  }

  result.original_actual_retail_price = originalActual
  result.original_product_retail_price = originalActual
  result.original_retail_price = originalRetail
  result.original_price = originalPrice
  result.actual_retail_price = Number((originalActual + signedProductAdj).toFixed(2))
  result.product_retail_price = result.actual_retail_price

  const installManuallyRemoved = isInstallManuallyRemoved(result)

  if (!installManuallyRemoved && hasActiveInstallCost(result)) {
    if (result.install_adj_value_type == null) result.install_adj_value_type = true
    if (result.install_adjustment == null) result.install_adjustment = 0
    if (result.install_quantity == null) result.install_quantity = 1

    let signedInstallAdj = getSignedInstallMarketingAdjustment(result)
    const originalInstall = resolveOriginalInstallCost(result)
    const displayedInstall = getDisplayedInstallPrice(result)

    if (
      Math.abs(displayedInstall - originalInstall) < 0.005 &&
      Math.abs(signedInstallAdj) > 0.005
    ) {
      result.install_adjustment = 0
      result.install_adj_value_type = true
      result.install_price_edited = false
      signedInstallAdj = 0
    }

    result.original_install_cost = originalInstall
    result.install_cost = Number((originalInstall + signedInstallAdj).toFixed(2))
    result.retail_price = Number(
      (Number(result.actual_retail_price ?? 0) + Number(result.install_cost ?? 0)).toFixed(2),
    )
  } else {
    result.retail_price = Number((originalRetail + signedProductAdj).toFixed(2))
  }

  return normalizeProductForSave(result)
}

export const reconcileCatalogMarketingPrices = (product) => {
  if (!product) return product
  const baselined = ensureCatalogBaselines(product)
  const synced = getCatalogProductWithAdjustment(baselined)
  return normalizeProductForSave({
    ...baselined,
    adjustment: synced.adjustment,
    adj_value_type: synced.adj_value_type,
    retail_price_edited: synced.retail_price_edited,
    actual_retail_price: synced.actual_retail_price,
    product_retail_price: synced.product_retail_price,
    retail_price: synced.retail_price,
    original_actual_retail_price: synced.original_actual_retail_price,
    original_product_retail_price: synced.original_product_retail_price,
    original_retail_price: synced.original_retail_price,
    original_price: synced.original_price,
    price: synced.price ?? baselined.price,
  })
}

export const hydrateEstimateLineMarketingPrices = (savedRow, catalogDefaults = {}) =>
  hydrateSavedOrderLinePrices(savedRow, catalogDefaults)

const resolveRequestServiceRowId = (service) => {
  if (service?.request_service_id != null && service?.request_service_id !== '') {
    return Number(service.request_service_id)
  }
  if (service?.backendId != null && service?.backendId !== '') {
    return Number(service.backendId)
  }
  if (
    service?.id != null &&
    service?.labour_cost_service_id != null &&
    Number(service.id) !== Number(service.labour_cost_service_id)
  ) {
    return Number(service.id)
  }
  return null
}

export const dedupeRequestServicesByRowId = (list) => {
  if (!Array.isArray(list) || list.length < 2) return list || []
  const seen = new Set()
  return list.filter((service) => {
    const rowId = resolveRequestServiceRowId(service)
    if (rowId == null) return true
    const key = String(rowId)
    if (seen.has(key)) return false
    seen.add(key)
    return true
  })
}

export const mapAdditionalServiceFromApi = (serviceItem, index = 0) => {
  const requestServiceId = resolveRequestServiceRowId(serviceItem)
  const storedProductIndex = Number(serviceItem?.product_index)
  const lineIndex = Number.isFinite(storedProductIndex) ? storedProductIndex : (index + 1)

  return {
    ...serviceItem,
    id: requestServiceId,
    request_service_id: requestServiceId,
    backendId: requestServiceId,
    labour_cost_service_id: serviceItem?.labour_cost_service_id ?? null,
    productId: serviceItem?.labour_cost_service_id ?? null,
    indexId: lineIndex,
    newIndexId: lineIndex,
    product_index: lineIndex,
  }
}

export const normalizeServiceForSave = (service) => {
  const requestServiceId = resolveRequestServiceRowId(service)
  const storedProductIndex = Number(service?.product_index)
  const productIndex = Number.isFinite(storedProductIndex)
    ? storedProductIndex
    : (service?.indexId ?? service?.newIndexId ?? 0)

  // Ensure labour_cost_service_id is sent, as QuickBooks sync fails when it is NULL.
  let lcsId = service?.labour_cost_service_id ?? service?.productId ?? null;
  // If this is a newly added service from the catalog, it won't have a requestServiceId
  // but its 'id' field contains the catalog's labour_cost_service_id
  if (lcsId == null && requestServiceId == null && service?.id != null) {
    lcsId = service.id;
  }

  return {
    ...service,
    request_service_id: requestServiceId,
    backendId: requestServiceId,
    product_index: productIndex,
    // labour_cost_service_id: service?.labour_cost_service_id ?? service?.productId ?? null,
    labour_cost_service_id: lcsId,
  }
}
